import { useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle, Zap, HelpCircle, Info } from 'lucide-react';
import * as XLSX from 'xlsx';
import { parseQuarterDataFromExcel, detectColumnMapping, validateQuarterData } from '@/lib/epseDataValidation';
import { DataQualityReport } from '@/types/epse';

interface EPSEDataUploadProps {
  onUpload: (data: ReturnType<typeof parseQuarterDataFromExcel>, detectedUnit?: 'kW' | 'kWh') => Promise<void>;
  isUploading: boolean;
  unitOverride?: 'auto' | 'kW' | 'kWh';
  onUnitOverrideChange?: (unit: 'auto' | 'kW' | 'kWh') => void;
  detectedUnit?: 'kW' | 'kWh';
}

export function EPSEDataUpload({ 
  onUpload, 
  isUploading, 
  unitOverride = 'auto', 
  onUnitOverrideChange,
  detectedUnit: externalDetectedUnit 
}: EPSEDataUploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const [connectionLimit, setConnectionLimit] = useState<number>(55);
  const [preview, setPreview] = useState<{
    rowCount: number;
    headers: string[];
    sampleRows: Record<string, unknown>[];
    mapping: ReturnType<typeof detectColumnMapping>;
    maxPeak: number;
    minValue: number;
    detectedUnit: 'kW' | 'kWh';
    calculatedAnnualKwh: number;
    dateRange: { start: Date; end: Date } | null;
  } | null>(null);
  const [qualityReport, setQualityReport] = useState<DataQualityReport | null>(null);
  const [parsedData, setParsedData] = useState<ReturnType<typeof parseQuarterDataFromExcel> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Use the externally detected unit if available, otherwise use from preview
  const currentDetectedUnit = externalDetectedUnit || preview?.detectedUnit;
  
  // Determine the effective unit (what we'll actually use)
  const effectiveUnit = unitOverride === 'auto' ? currentDetectedUnit : unitOverride;
  
  // Check if there's a mismatch between detected and selected
  const hasUnitMismatch = currentDetectedUnit && unitOverride !== 'auto' && unitOverride !== currentDetectedUnit;
  
  const processFile = useCallback(async (file: File) => {
    setFile(file);
    setQualityReport(null);
    
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer);
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet);
    
    if (jsonData.length === 0) {
      return;
    }
    
    const headers = Object.keys(jsonData[0] as Record<string, unknown>);
    const mapping = detectColumnMapping(headers);
    
    // Parse all data
    const parsed = parseQuarterDataFromExcel(jsonData as Record<string, unknown>[], mapping);
    setParsedData(parsed);
    
    // Calculate stats
    const values = parsed.map(p => p.kw_value);
    const maxPeak = Math.max(...values);
    const minValue = Math.min(...values);
    
    // Calculate annual kWh from quarter data (kW × 0.25h = kWh per quarter)
    const calculatedAnnualKwh = parsed.reduce((sum, q) => sum + q.kw_value * 0.25, 0);
    
    // Get date range
    let dateRange: { start: Date; end: Date } | null = null;
    if (parsed.length > 0) {
      const sortedDates = parsed
        .map(p => new Date(p.start_time))
        .sort((a, b) => a.getTime() - b.getTime());
      dateRange = {
        start: sortedDates[0],
        end: sortedDates[sortedDates.length - 1],
      };
    }
    
    const detectedUnit = mapping.detected_unit || 'kW';
    
    setPreview({
      rowCount: jsonData.length,
      headers,
      sampleRows: jsonData.slice(0, 5) as Record<string, unknown>[],
      mapping,
      maxPeak,
      minValue,
      detectedUnit,
      calculatedAnnualKwh,
      dateRange,
    });
    
    // Validate without comparing to declared annual (measured mode doesn't need that check)
    const report = validateQuarterData(parsed as any);
    setQualityReport(report);
  }, []);
  
  const handleDrop = useCallback(async (files: File[]) => {
    if (files.length > 0) {
      await processFile(files[0]);
    }
  }, [processFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
    onDrop: handleDrop,
    inputRef,
  });
  
  const handleUpload = async () => {
    if (!parsedData) return;
    await onUpload(parsedData, effectiveUnit);
    setFile(null);
    setPreview(null);
    setParsedData(null);
    setQualityReport(null);
  };

  const expectedExceedances = preview && parsedData 
    ? parsedData.filter(p => p.kw_value > connectionLimit).length 
    : 0;
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5" />
          Kwartierdata Uploaden
        </CardTitle>
        <CardDescription>
          Upload een Excel bestand met kwartierwaarden (kW of kWh)
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Column recognition info */}
        <Alert className="bg-muted/50">
          <Info className="h-4 w-4" />
          <AlertDescription>
            <strong>Kolom herkenning:</strong>
            <ul className="list-disc list-inside mt-1 text-sm space-y-0.5">
              <li><code className="bg-muted px-1 rounded text-xs">Piek</code>, <code className="bg-muted px-1 rounded text-xs">Belasting</code>, <code className="bg-muted px-1 rounded text-xs">Vermogen</code>, <code className="bg-muted px-1 rounded text-xs">kW</code> → kW waarden (direct)</li>
              <li><code className="bg-muted px-1 rounded text-xs">Afname</code>, <code className="bg-muted px-1 rounded text-xs">Verbruik</code>, <code className="bg-muted px-1 rounded text-xs">Energie</code>, <code className="bg-muted px-1 rounded text-xs">kWh</code> → kWh waarden (×4 naar kW)</li>
            </ul>
          </AlertDescription>
        </Alert>

        {/* Manual Unit Override */}
        {onUnitOverrideChange && (
          <div className="p-4 border rounded-lg bg-muted/30 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                Eenheid selectie
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>Kies handmatig de eenheid als de automatische detectie niet correct is. kWh wordt automatisch omgerekend naar kW (×4).</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Label>
              {currentDetectedUnit && (
                <Badge variant="outline" className="flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Gedetecteerd: {currentDetectedUnit}
                </Badge>
              )}
            </div>
            
            <RadioGroup
              value={unitOverride}
              onValueChange={(v) => onUnitOverrideChange(v as 'auto' | 'kW' | 'kWh')}
              className="flex flex-wrap gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="auto" id="unit-auto" />
                <Label htmlFor="unit-auto" className="cursor-pointer">Automatisch</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="kW" id="unit-kw" />
                <Label htmlFor="unit-kw" className="cursor-pointer">kW (vermogen)</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="kWh" id="unit-kwh" />
                <Label htmlFor="unit-kwh" className="cursor-pointer">kWh (energie per kwartier)</Label>
              </div>
            </RadioGroup>
            
            {hasUnitMismatch && (
              <Alert variant="destructive" className="mt-2">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Let op:</strong> De kolomnaam suggereert <strong>{currentDetectedUnit}</strong>, maar u heeft <strong>{unitOverride}</strong> geselecteerd. 
                  Controleer of dit correct is, anders kan de simulatie onnauwkeurige resultaten geven.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* Connection limit input */}
        <div className="space-y-2">
          <Label htmlFor="connection_limit_upload" className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-amber-500" />
            Contractgrens (kW)
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>Het maximaal vermogen dat u mag afnemen volgens uw netaansluiting contract.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Label>
          <Input
            id="connection_limit_upload"
            type="number"
            value={connectionLimit}
            onChange={(e) => setConnectionLimit(Number(e.target.value))}
            min={0}
            step={10}
            className="w-40"
          />
        </div>

        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
            transition-colors
            ${isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}
          `}
        >
          <input {...getInputProps()} />
          <Upload className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
          {isDragActive ? (
            <p className="text-primary">Sleep het bestand hier...</p>
          ) : (
            <div>
              <p className="font-medium">Sleep een bestand of klik om te selecteren</p>
              <p className="text-sm text-muted-foreground mt-1">
                Ondersteunde formaten: .xlsx, .xls, .csv
              </p>
            </div>
          )}
        </div>
        
        {preview && (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-medium">{file?.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {preview.rowCount.toLocaleString('nl-NL')} rijen gevonden
                  </p>
                </div>
              </div>
            </div>
            
            <div className="text-sm">
              <p className="font-medium mb-2">Gedetecteerde kolommen:</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Timestamp:</span>
                  <code className="text-xs bg-muted px-1 rounded">
                    {preview.mapping.start_time || 'Niet gevonden'}
                  </code>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Vermogen:</span>
                  <code className="text-xs bg-muted px-1 rounded">
                    {preview.mapping.kw_value || preview.mapping.kwh_value || 'Niet gevonden'}
                  </code>
                  <Badge variant={hasUnitMismatch ? 'destructive' : 'outline'} className="text-xs">
                    {effectiveUnit || preview.detectedUnit}
                    {hasUnitMismatch && ' (handmatig)'}
                  </Badge>
                </div>
                {preview.mapping.pv_kw && (
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">PV kW:</span>
                    <code className="text-xs bg-muted px-1 rounded">
                      {preview.mapping.pv_kw}
                    </code>
                  </div>
                )}
              </div>
            </div>

            {/* Data summary */}
            <div className="p-3 bg-muted/30 rounded-lg space-y-2">
              <p className="font-medium text-sm">Data Samenvatting</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                {/* Calculated annual kWh - prominent */}
                <div className="flex justify-between col-span-2 p-2 bg-primary/10 rounded">
                  <span className="text-muted-foreground">Berekend jaarverbruik:</span>
                  <span className="font-bold text-primary">
                    {Math.round(preview.calculatedAnnualKwh).toLocaleString('nl-NL')} kWh
                  </span>
                </div>
                {/* Date range */}
                {preview.dateRange && (
                  <div className="flex justify-between col-span-2">
                    <span className="text-muted-foreground">Periode:</span>
                    <span className="font-medium">
                      {preview.dateRange.start.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })} - {preview.dateRange.end.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Max piek:</span>
                  <span className={`font-medium ${preview.maxPeak > connectionLimit ? 'text-red-600' : ''}`}>
                    {preview.maxPeak.toFixed(1)} kW
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Contractgrens:</span>
                  <span className="font-medium">{connectionLimit} kW</span>
                </div>
                <div className="flex justify-between col-span-2">
                  <span className="text-muted-foreground">Verwachte overschrijdingen:</span>
                  <span className={`font-medium ${expectedExceedances > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {expectedExceedances.toLocaleString('nl-NL')} kwartieren
                  </span>
                </div>
              </div>
            </div>
            
            {qualityReport && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Datakwaliteit</span>
                  <span className={`text-sm font-medium ${
                    qualityReport.coverage_score >= 90 ? 'text-green-600' :
                    qualityReport.coverage_score >= 70 ? 'text-amber-600' :
                    'text-red-600'
                  }`}>
                    {qualityReport.coverage_score}%
                  </span>
                </div>
                <Progress 
                  value={qualityReport.coverage_score} 
                  className="h-2"
                />
                
                {qualityReport.warnings.length > 0 && (
                  <div className="space-y-2">
                    {qualityReport.warnings.map((warning, i) => (
                      <Alert 
                        key={i} 
                        variant={warning.severity === 'error' ? 'destructive' : 'default'}
                      >
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>{warning.message}</AlertDescription>
                      </Alert>
                    ))}
                  </div>
                )}
                
                {qualityReport.warnings.length === 0 && (
                  <Alert>
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription>
                      Data ziet er goed uit! Geen problemen gedetecteerd.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
            
            <Button 
              onClick={handleUpload} 
              disabled={isUploading || !parsedData || parsedData.length === 0}
              className="w-full"
            >
              {isUploading ? 'Uploaden...' : `${parsedData?.length.toLocaleString('nl-NL') || 0} kwartierwaarden uploaden`}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Simple dropzone hook implementation
function useDropzone(options: {
  accept: Record<string, string[]>;
  maxFiles: number;
  onDrop: (files: File[]) => void;
  inputRef: React.RefObject<HTMLInputElement>;
}) {
  const [isDragActive, setIsDragActive] = useState(false);
  
  const getRootProps = () => ({
    onClick: () => options.inputRef.current?.click(),
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragActive(true);
    },
    onDragLeave: () => setIsDragActive(false),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragActive(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        options.onDrop(files.slice(0, options.maxFiles));
      }
    },
  });
  
  const getInputProps = () => ({
    ref: options.inputRef,
    type: 'file' as const,
    accept: Object.values(options.accept).flat().join(','),
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length > 0) {
        options.onDrop(files.slice(0, options.maxFiles));
      }
    },
    style: { display: 'none' },
  });
  
  return { getRootProps, getInputProps, isDragActive };
}
